import { storeToRefs } from "pinia";
import { useNgSession } from "@sazka/ng/ng-session";
import { DataLayer } from "@sazka/utils/data-layer";
import { useRegistrationStore } from "../composables/use-player-registration-data";
import useRegistrationPersonalData from "../composables/use-registration-personal-data";

export namespace RegistrationAnalytics {
    const { session } = useNgSession();

    export let isAnalyticsSuppressed = false; // used to prevent duplication of validation events
    export const suppressAnalytics = () => {
        isAnalyticsSuppressed = true;
    };

    export const unsuppressAnalytics = () => {
        isAnalyticsSuppressed = false;
    };

    const getOfferedBonus = () => {
        const { bonuses } = window.registrationConfiguration;
        return bonuses?.map((x) => x.codeName).join(",");
    };

    const getDefaultPageData = async (stepName: string) => {
        const { bonuses, preselectedBonusId } = window.registrationConfiguration;
        const { user, progress, isDataSetPromise } = storeToRefs(useRegistrationStore());
        const { registrationPersonalData } = useRegistrationPersonalData();

        let selectedBonusName: string | undefined = undefined;

        if (user.value?.registrationVertical) {
            selectedBonusName = bonuses?.find((x) => x.vertical == user.value.registrationVertical)?.codeName;
        }

        await isDataSetPromise.value;
        let flow: "manual" | "bankID" | null =
            registrationPersonalData.value.isBankIdUsed || progress.value?.isPersonalDataVerified ? "bankID" : "manual";
        if (progress.value?.areGamingLimitsSet && progress.value?.isPersonalDataVerified) {
            flow = null;
        }

        return {
            type: "account.registration",
            kind: `widget.registration.${stepName}`,
            verticalCode: "no_vertical",
            verticalCodeStrict: "no_vertical",
            gameCodeStrict: "no_game",
            selectedBonus: selectedBonusName,
            presentedVertical: preselectedBonusId ? bonuses?.find((x) => x.id == preselectedBonusId)?.codeName : null,
            flow,
        };
    };

    /* --- DL2 --- */
    export const pushBonusPopup = () => {
        // DL2.00
        DataLayer.push({
            page: {
                type: "account.registration",
                kind: "widget.registration.bonusPopup",
                offeredBonus: getOfferedBonus(),
                verticalCode: "no_vertical",
                verticalCodeStrict: "no_vertical",
                gameCodeStrict: "no_game",
            },
            event: "page",
        });
    };

    export const pushSelectBonus = (selectedBonusId: number) => {
        // DL2.05
        const { bonuses } = window.registrationConfiguration;
        const selectedBonus = bonuses?.find((x) => x.id == selectedBonusId)?.codeName;

        DataLayer.push({
            event: "action.clickButton.registration",
            action: {
                clickButton: {
                    label: "selectBonus",
                    eventProperty1: selectedBonus,
                },
            },
            page: {
                selectedBonus: selectedBonus,
                offeredBonus: getOfferedBonus(),
            },
        });
    };

    export const pushStep1 = async () => {
        // DL2.10
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("step1")),
                flow: undefined,
            },
            event: "page",
        });
    };

    export const pushBonusAndOffers = (bonusAndOffers: boolean) => {
        DataLayer.push({
            page: {
                bonusAndOffers: bonusAndOffers.toString(),
            },
        });
    };

    export const pushStep2 = async () => {
        // DL2.20
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("step2")),
                flow: undefined,
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushCreateUser = (playerId: string, bonusId: number) => {
        // DL2.20
        const { bonuses } = window.registrationConfiguration;

        DataLayer.push({
            event: "action.create.user",
            user: {
                id: playerId,
                selectedBonus: bonuses?.find((x) => x.id == bonusId)?.codeName ?? "",
            },
        });
    };

    export const pushSelectFlow = (flow: "manual" | "bankID") => {
        // DL2.25
        DataLayer.push({
            page: {
                flow,
            },
        });
    };

    export const pushStep3 = async (isBankIdUsed = false) => {
        // DL2.30
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("step3")),
                flow: isBankIdUsed ? "bankID" : "manual",
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushStepVerifyEmail = async () => {
        // DL2.31
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("verifyEmail")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushStepVerifyEmailSuccess = async () => {
        // DL2.32
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("verifyEmailSuccess")),
            },
            event: "page",
        });
    };

    type AnalyticsVerify = "email" | "personalInfo" | "deposit" | "gamingLimits";
    export const pushVerified = (verification: AnalyticsVerify) => {
        // DL2.00a
        DataLayer.push({
            event: `action.${verification}Verified.player`,
        });
    };

    export const pushStepLimits = async () => {
        // DL2.33
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("setGameLimit")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushStepVerifyPersonalInfo = async () => {
        // DL2.34
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("verifyPersonalInfo")),
                flow: "manual",
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushStepDepositMoney = async () => {
        // DL2.35
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("depositMoney")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushRegisterPaymentMethod = async () => {
        // DL2.36
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("registerPaymentMethod")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushStep4 = async () => {
        // DL2.40
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("step4")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushInProgress = async () => {
        // DL2.41
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("inProgress")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushPOSverifyPersonalInfo = async () => {
        // DL2.34
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("POSverifyPersonalInfo")),
                flow: "manual",
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushPending = async () => {
        // DL2.42
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("pending")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushExistingEmail = async () => {
        // DL2.50
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("existingEmail")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    export const pushDenied = async () => {
        // DL2.60
        DataLayer.push({
            page: {
                ...(await getDefaultPageData("deniedRegistration")),
            },
            user: {
                id: session.value?.PlayerID,
            },
            event: "page",
        });
    };

    /* --- DL3 --- */

    export const pushFieldFocus = (fieldName: string) => {
        DataLayer.push({
            event: "action.focusField.registration",
            action: {
                focusField: {
                    field: fieldName,
                },
            },
        });
    };

    export const pushSuccessfulFieldValidation = (fieldName: string) => {
        if (isAnalyticsSuppressed) {
            return;
        }

        DataLayer.push({
            event: "action.successfulFieldValidation.registration",
            action: {
                successfulFieldValidation: {
                    field: fieldName,
                    time: Date.now(),
                },
            },
        });
    };

    export const pushFirstFieldFocus = (fieldName: string) => {
        DataLayer.push({
            event: "action.firstFocusField.registration",
            action: {
                firstFocusField: {
                    field: fieldName,
                    time: Date.now(),
                },
            },
        });
    };

    export const pushFieldUnfocus = (fieldName: string) => {
        const unfocusField = {
            field: fieldName,
        };

        DataLayer.push({
            event: "action.unfocusField.registration",
            action: {
                unfocusField,
            },
        });
    };

    export const pushFieldError = (fieldName: string, errorMessage: string, errorDetail: string) => {
        const errorField = {
            field: fieldName,
            message: errorMessage,
            errorDetail: errorDetail,
        };

        DataLayer.push({
            event: "action.errorField.registration",
            action: {
                errorField,
            },
        });
    };

    export const pushSelectBank = (bank: string, position: number) => {
        DataLayer.push({
            event: "action.selectBank.registration",
            action: {
                selectBank: {
                    bank,
                    position: position.toString(),
                },
            },
        });
    };

    export const pushBankIdentitySuccess = (bank: string) => {
        const bankIdentitySuccess = {
            bank: bank,
        };

        DataLayer.push({
            event: "action.bankIdentitySuccess.registration",
            action: {
                bankIdentitySuccess,
            },
        });
    };

    export const pushBankIdentityFail = () => {
        const bankIdentityFailure = {
            errorMessage: "Vaše banka nám neposkytla všechny potřebné údaje",
        };

        DataLayer.push({
            event: "action.bankIdentityFailure.registration",
            action: {
                bankIdentityFailure,
            },
        });
    };

    export const pushButtonClick = (button: string) => {
        const clickButton = {
            label: button,
        };

        DataLayer.push({
            event: "action.clickButton.registration",
            action: {
                clickButton,
            },
        });
    };

    export const pushError = (errorText: string) => {
        const failed = {
            errorMessage: errorText,
        };

        DataLayer.push({
            event: "action.failed.registration",
            action: {
                failed,
            },
        });
    };

    export const pushFullRegistration = () => {
        DataLayer.push({
            event: "action.fullRegistration.user",
            user: {
                id: session.value?.PlayerID,
            },
        });
    };

    export const pushLoginClick = () => {
        DataLayer.pushInstant({
            event: "action.click.navigation",
            action: {
                click: {
                    name: "loginFromRegistration",
                },
            },
        });
    };

    export const pushDownloadApp = (platform: "android" | "ios") => {
        DataLayer.push({
            event: "action.download.app",
            action: {
                download: {
                    name: "sazka",
                    platform,
                },
            },
        });
    };

    /* --- DL4 --- */
    export const pushBannerImpress = (codeName: string, imageUrl: string, isLastStep: boolean) => {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: codeName,
                            name: isLastStep ? "registration.completed" : "registration.step1",
                            position: isLastStep ? "registration.completed.bottom" : "registration.step1.top",
                            creative: imageUrl,
                        },
                    ],
                },
            },
            page: {
                presentedVertical: codeName,
            },
        });
    };

    const pushDocumentDefault = async (stepName: string) => {
        DataLayer.push({
            page: {
                ...(await getDefaultPageData(stepName)),
                offeredBonus: getOfferedBonus(),
                flow: "manual",
            },
            event: "page",
        });
    };

    /* --- DL5 --- */
    export const pushQrScan = () => pushDocumentDefault("QRScan");

    export const pushQrScanSuccess = () => pushDocumentDefault("QRScanSuccess");

    export const pushOcrDocumentIntro = () => pushDocumentDefault("verifyPersonalInfoDocuments.1");

    export const pushOcrSelfieIntro = () => pushDocumentDefault("verifyPersonalInfoDocuments.2");

    export const pushOcrFrontSide = () => pushDocumentDefault("verifyPersonalInfoDocuments.frontSideScan");

    export const pushOcrBackSide = () => pushDocumentDefault("verifyPersonalInfoDocuments.backSideScan");

    export const pushOcrCheckPhoto = () => pushDocumentDefault("verifyPersonalInfoDocuments.checkPhoto");

    export const pushOcrButtonVerify = () => pushButtonClick("verifyPhoto");

    export const pushOcrButtonRetake = () => pushButtonClick("retakePhoto");

    export const pushOcrButtonFace = () => pushButtonClick("faceControl");

    export const pushOcrFaceProgress = (progress: number) =>
        pushDocumentDefault(`verifyPersonalInfoDocuments.faceScan.${progress + 1}`);

    export const pushOcrDocumentPending = () => pushDocumentDefault("verifyPersonalInfoDocuments.pending");

    export const pushOcrDocumentSuccess = () => pushDocumentDefault("verifyPersonalInfoDocuments.success");

    export const pushOcrError = (errorText: string) => {
        const failedVerification = {
            errorMessage: errorText,
        };

        DataLayer.push({
            event: "action.failedVerification.registration",
            action: {
                failed: failedVerification,
            },
        });
    };

    export const pushOcrDepositRejected = () => pushDocumentDefault("depositRejected");
}
